/**
 * React Router 7 Client Entry Point
 *
 * This file hydrates the React application on the client side.
 */

import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { HydratedRouter } from "react-router/dom";
import i18next from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { getInitialNamespaces } from "remix-i18next/client";

import i18nConfig from "./i18n/config";

// Initialise i18next BEFORE hydrating so the client's first render matches the
// server's. `htmlTag` detection reads the <html lang> the server set, so the
// client reuses the exact server language — no hydration mismatch. The http
// backend lazy-fetches catalogs from `/locales/<lng>/<ns>.json`; namespaces the
// server rendered are preloaded via `getInitialNamespaces()` (reads route
// `handle.i18n` annotations) so hydration has them immediately, and further
// namespaces load on demand per route.
const hydrate = () =>
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <I18nextProvider i18n={i18next}>
          <HydratedRouter />
        </I18nextProvider>
      </StrictMode>,
    );
  });

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(Backend)
  .init({
    ...i18nConfig,
    ns: getInitialNamespaces(),
    defaultNS: "common",
    // htmlTag is authoritative (set by SSR from session preferredLanguage).
    // localStorage is a secondary cache so the correct language is ready before
    // the first SSR response on a fresh tab — it is always overwritten by htmlTag.
    detection: { order: ["htmlTag", "localStorage"], caches: ["localStorage"] },
    backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
    interpolation: { escapeValue: false },
  })
  .then(hydrate)
  .catch((err) => {
    // i18next init failed (e.g. network error fetching catalogs). Log the error
    // but still hydrate so the app renders in its fallback language rather than
    // showing a blank screen.
    console.error("[i18next] init failed, hydrating with fallback language:", err);
    hydrate();
  });
